import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth';
import { ThemeSelector } from '@/components/ui/theme-selector';
import { useLastSeen } from '@/hooks/useLastSeen';
import { useVisibilityRefresh } from '@/hooks/useVisibilityRefresh';
import { useUnreadNotifications } from '@/hooks/useUnreadNotifications';
import { RefreshButton } from '@/components/ui/refresh-button';
import { CalendarPickerV2 as CalendarPicker } from '@/components/ui/calendar-picker-v2';
import { TokenExpirationWarning } from '@/components/settings/TokenExpirationWarning';
import { AnimatedBackground } from '@/components/ui/animated-background';
import { VersionBadge } from '@/components/ui/version-badge';
import { Badge } from '@/components/ui/badge';
import { useDateRange } from '@/contexts/DateRangeContext';
import { useTimezone } from '@/contexts/TimezoneContext';
import { useFilters } from '@/contexts/FilterContext';
import { useCardStyle } from '@/features/theme/contexts/ThemeContext';
import { FilterSidebar, FILTER_SIDEBAR_WIDTH } from '@/components/dashboard/FilterSidebar';
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Home,
  Settings,
  LogOut,
  Users,
  ChevronRight,
  ChevronDown,
  BarChart,
  Calendar,
  Clock,
  DollarSign,
  Globe,
  ShoppingCart,
  Video,
  RotateCcw,
  Wallet,
  History,
  AlertTriangle,
  Megaphone,
  LayoutTemplate,
  FlaskConical,
  ShieldCheck,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator,
  SidebarTrigger,
} from '@/components/ui/sidebar';

export function MainLayout() {
  const { profile, signOut, hasStoreRestriction, restrictedStore } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { dateRange, setDateRange } = useDateRange();
  const { timezone, timezoneInfo } = useTimezone();
  const { isSidebarHidden } = useFilters();
  const { cardStyleObject } = useCardStyle();
  // Rastrear última vez que usuário esteve online
  useLastSeen();

  // Detectar quando aba volta ao foco e resetar queries travadas
  useVisibilityRefresh();

  // Sistema de notificações não lidas
  const { unreadCounts, markAsViewed } = useUnreadNotifications();

  // Marcar página como visualizada quando acessar
  useEffect(() => {
    if (location.pathname === '/changelog') {
      markAsViewed('changelog');
    } else if (location.pathname === '/admin/postback-errors') {
      markAsViewed('postback_errors');
    }
  }, [location.pathname, markAsViewed]);

  // Estado para hora atual
  const [currentTime, setCurrentTime] = useState(new Date());
  const [campanhasOpen, setCampanhasOpen] = useState(false);

  // Estado para avatar do usuário
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  // Atualizar hora a cada minuto
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Atualiza a cada minuto

    return () => clearInterval(timer);
  }, []);

  // Buscar avatar do usuário
  useEffect(() => {
    const fetchAvatar = async () => {
      if (profile?.equipe_id) {
        const { data } = await supabase
          .schema('grupod12')
          .from('equipe')
          .select('avatar_url')
          .eq('id', profile.equipe_id)
          .single();

        if (data?.avatar_url) {
          setAvatarUrl(data.avatar_url);
        }
      }
    };
    fetchAvatar();
  }, [profile?.equipe_id]);

  // Navegação organizada em grupos visuais (sem labels)
  // SEGURANÇA: Se usuário tem loja restrita, mostra apenas itens relevantes
  const navigationGroups = hasStoreRestriction
    ? [
        // Navegação simplificada para usuários com loja restrita
        // Grupo 1: Dashboard principal
        [
          { name: `Dashboard ${restrictedStore}`, href: '/', icon: Home }
        ],
        // Grupo 2: Vendas e Devoluções
        [
          { name: 'Vendas', href: '/sales', icon: ShoppingCart },
          { name: 'Devoluções', href: '/reports/refunds', icon: RotateCcw }
        ],
        // Grupo 3: DRE (relatório financeiro)
        [
          { name: 'DRE', href: '/reports/dre', icon: DollarSign }
        ]
      ]
    : [
        // Navegação completa para usuários sem restrição
        // Grupo 1: Dashboards
        [
          { name: 'D12 Dash', href: '/', icon: Home }
        ],
        // Grupo 2: Vendas e Análise
        [
          { name: 'Vendas', href: '/sales', icon: ShoppingCart },
          { name: 'Devoluções', href: '/reports/refunds', icon: RotateCcw },
          { name: 'Tracker', href: '/tracker', icon: BarChart, disabled: true },
          {
            name: 'Campanhas',
            icon: Megaphone,
            subItems: [
              { name: 'Criativos', href: '/creativesv2', icon: Video },
              { name: 'Campanhas', href: '/campanhas', icon: Megaphone },
              { name: 'Landers', href: '/landers', icon: LayoutTemplate, soon: true },
            ]
          },
          { name: 'Tech Lab', href: '/tech-lab', icon: FlaskConical }
        ],
        // Grupo 3: DRE e Distribuição de Lucros
        [
          { name: 'DRE', href: '/reports/dre', icon: DollarSign },
          { name: 'Distribuição de Lucros', href: '/distribuicao-lucros', icon: Wallet }
        ]
      ];

  const adminNavigation = [
    { name: 'Financeiro', href: '/financeiro', icon: Wallet, roles: ['admin', 'head'], notificationKey: null },
    { name: 'Auditoria', href: '/auditoria', icon: ShieldCheck, roles: ['admin'], notificationKey: null },
    { name: 'Equipe', href: '/team', icon: Users, roles: ['admin'], notificationKey: null },
    { name: 'Atualizações', href: '/changelog', icon: History, roles: ['admin', 'head'], notificationKey: 'changelog' as const },
    { name: 'Auditoria de Pedidos', href: '/admin/postback-errors', icon: AlertTriangle, roles: ['admin', 'head'], notificationKey: 'postback_errors' as const },
    { name: 'Configurações', href: '/settings', icon: Settings, roles: ['admin', 'head'], notificationKey: null },
  ];

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <>
      {/* Animated gradient background */}
      <AnimatedBackground />

      <SidebarProvider defaultOpen={true}>
        <Sidebar
          variant="inset"
          collapsible="icon"
          className="border-0"
        >
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" asChild>
                <Link to="/" className="flex items-center gap-2">
                  <img
                    src="/logo-d12_sem_fundo.png"
                    alt={hasStoreRestriction ? restrictedStore || 'Hub' : 'Grupo D12'}
                    className="h-8 w-8 object-contain"
                  />
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {hasStoreRestriction ? restrictedStore : 'Grupo D12'}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {hasStoreRestriction ? 'Painel de Afiliados' : 'Hub Operacional'}
                    </span>
                  </div>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
        
        <SidebarContent>
          {/* Grupos de navegação sem labels, com separadores visuais */}
          {navigationGroups.map((group, groupIndex) => (
            <div key={groupIndex}>
              <SidebarGroup className="py-0">
                <SidebarGroupContent className="py-2">
                  <SidebarMenu>
                    {group.map((item) => {
                      // Item collapsible (ex: Campanhas com subitens)
                      if ('subItems' in item) {
                        const isAnySubActive = item.subItems.some(sub => isActive(sub.href));
                        return (
                          <SidebarMenuItem key={item.name}>
                            <Collapsible open={campanhasOpen} onOpenChange={setCampanhasOpen}>
                              <CollapsibleTrigger asChild>
                                <SidebarMenuButton
                                  isActive={!campanhasOpen && isAnySubActive}
                                  tooltip={item.name}
                                  className="w-full"
                                >
                                  <item.icon className="size-4" />
                                  <span>{item.name}</span>
                                  <ChevronDown
                                    className={`ml-auto size-3.5 transition-transform duration-200 ${campanhasOpen ? 'rotate-180' : ''}`}
                                  />
                                </SidebarMenuButton>
                              </CollapsibleTrigger>
                              <CollapsibleContent className="overflow-hidden">
                                <SidebarMenu className="pl-2 pt-1 mt-1">
                                  {item.subItems.map((sub) => (
                                    <SidebarMenuItem key={sub.name}>
                                      <SidebarMenuButton
                                        asChild
                                        isActive={isActive(sub.href)}
                                        size="sm"
                                      >
                                        <Link to={sub.href} className="flex items-center justify-between w-full">
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm">{sub.name}</span>
                                          </div>
                                          {'soon' in sub && sub.soon && (
                                            <Badge
                                              variant="secondary"
                                              className="ml-auto h-5 px-1.5 text-[10px] font-semibold bg-yellow-500/20 text-yellow-500 border-yellow-500/30"
                                            >
                                              SOON
                                            </Badge>
                                          )}
                                        </Link>
                                      </SidebarMenuButton>
                                    </SidebarMenuItem>
                                  ))}
                                </SidebarMenu>
                              </CollapsibleContent>
                            </Collapsible>
                          </SidebarMenuItem>
                        );
                      }

                      // Item normal
                      return (
                        <SidebarMenuItem key={item.name}>
                          {'disabled' in item && item.disabled ? (
                            <SidebarMenuButton
                              tooltip={`${item.name} (Em breve)`}
                              className="opacity-40 cursor-not-allowed"
                            >
                              <item.icon className="size-4" />
                              <span>{item.name}</span>
                            </SidebarMenuButton>
                          ) : (
                            <SidebarMenuButton
                              asChild
                              isActive={isActive(item.href)}
                              tooltip={item.name}
                            >
                              <Link to={item.href} className="flex items-center justify-between w-full">
                                <div className="flex items-center gap-2">
                                  <item.icon className="size-4" />
                                  <span>{item.name}</span>
                                </div>
                                {'soon' in item && item.soon && (
                                  <Badge
                                    variant="secondary"
                                    className="ml-auto h-5 px-1.5 text-[10px] font-semibold bg-yellow-500/20 text-yellow-500 border-yellow-500/30"
                                  >
                                    SOON
                                  </Badge>
                                )}
                              </Link>
                            </SidebarMenuButton>
                          )}
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
              {/* Separador entre grupos (exceto após o último) */}
              {groupIndex < navigationGroups.length - 1 && (
                <SidebarSeparator className="my-2" />
              )}
            </div>
          ))}

          {/* Separador antes do grupo Admin */}
          {/* SEGURANÇA: Ocultar seção admin para usuários com loja restrita */}
          {!hasStoreRestriction && (profile?.role === 'admin' || profile?.role === 'head') && (
            <>
              <SidebarSeparator className="my-2" />
              <SidebarGroup className="py-0">
                <SidebarGroupContent className="py-2">
                  <SidebarMenu>
                    {adminNavigation
                      .filter(item => !item.roles || item.roles.includes(profile.role))
                      .map((item) => {
                        const notificationCount = item.notificationKey
                          ? unreadCounts[item.notificationKey]
                          : 0;

                        return (
                          <SidebarMenuItem key={item.name}>
                            <SidebarMenuButton
                              asChild
                              isActive={isActive(item.href)}
                              tooltip={notificationCount > 0 ? `${item.name} (${notificationCount} novo${notificationCount > 1 ? 's' : ''})` : item.name}
                            >
                              <Link to={item.href} className="flex items-center justify-between w-full">
                                <div className="flex items-center gap-2">
                                  <item.icon className="size-4" />
                                  <span>{item.name}</span>
                                </div>
                                {notificationCount > 0 && (
                                  <Badge
                                    variant="secondary"
                                    className="ml-auto h-5 min-w-5 px-1.5 text-xs font-medium bg-primary/20 text-primary border-primary/30 hover:bg-primary/30"
                                  >
                                    {notificationCount}
                                  </Badge>
                                )}
                              </Link>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        );
                      })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </>
          )}
        </SidebarContent>

        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <div className="flex items-center justify-between w-full">
                {/* User Profile with Avatar */}
                <SidebarMenuButton asChild className="flex-1 max-w-[calc(100%-3rem)]">
                  <Link to="/profile">
                    <Avatar className="size-8 border border-primary/20">
                      <AvatarImage src={avatarUrl || undefined} alt={profile?.full_name || 'Avatar'} />
                      <AvatarFallback className="bg-primary/20 text-primary font-medium text-sm">
                        {profile?.full_name?.charAt(0).toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col items-start flex-1 min-w-0">
                      <span className="text-sm font-medium truncate w-full">
                        {profile?.full_name || 'Usuário'}
                      </span>
                      <span className="text-xs text-muted-foreground capitalize">
                        {profile?.role || 'user'}
                      </span>
                    </div>
                  </Link>
                </SidebarMenuButton>

                {/* Logout Icon Only */}
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/20 transition-colors"
                  title="Sair"
                >
                  <LogOut className="size-4" />
                </button>
              </div>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      <SidebarInset>
        <header
          className="sticky top-0 z-30 flex h-16 items-center gap-4 px-6"
          data-theme-surface="true"
          style={cardStyleObject}
        >
          <SidebarTrigger className="-ml-1 hover:bg-accent hover:text-accent-foreground transition-colors" />

          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Dashboard</span>
            <ChevronRight className="size-3" />
            <span className="text-foreground font-medium">
              {navigationGroups.flat().find(item => 'href' in item && isActive(item.href))?.name ||
               navigationGroups.flat().flatMap(item => 'subItems' in item ? item.subItems : []).find(sub => isActive(sub.href))?.name ||
               adminNavigation.find(item => isActive(item.href))?.name ||
               'Página'}
            </span>
          </div>

          <div className="flex-1" />

          {/* System Info - Timezone, Date, Time, Currency */}
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            {/* Timezone - clickable to go to settings */}
            <Link
              to="/settings#timezone"
              className="flex items-center gap-1.5 hover:text-primary transition-colors cursor-pointer"
              title={`Timezone: ${timezoneInfo.label} - Clique para alterar`}
            >
              <Globe className="size-3.5" />
              <span>{timezoneInfo.label.split(' ')[0]} UTC{timezoneInfo.offset >= 0 ? '+' : ''}{timezoneInfo.offset}</span>
            </Link>

            <span className="text-border/50">•</span>

            {/* Date - formatted in selected timezone */}
            <div className="flex items-center gap-1.5">
              <Calendar className="size-3.5" />
              <span className="font-mono">
                {currentTime.toLocaleDateString('pt-BR', {
                  timeZone: timezone,
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                })}
              </span>
            </div>

            <span className="text-border/50">•</span>

            {/* Time - formatted in selected timezone */}
            <div className="flex items-center gap-1.5">
              <Clock className="size-3.5" />
              <span className="font-mono font-medium">
                {currentTime.toLocaleTimeString('pt-BR', {
                  timeZone: timezone,
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                })}
              </span>
            </div>

            <span className="text-border/50">•</span>

            {/* Currency */}
            <div className="flex items-center gap-1.5">
              <DollarSign className="size-3.5" />
              <span>USD</span>
            </div>
          </div>

          <div className="h-4 w-px bg-white/10 mx-2" />

          {/* Right side controls */}
          <div className="flex items-center gap-2">
            {/* Date Range Picker - Simplified */}
            <CalendarPicker
              value={dateRange}
              onChange={setDateRange}
            />

            <div className="h-4 w-px bg-white/10 mx-2" />

            {/* Refresh Button - Icon only */}
            <RefreshButton iconOnly />

            {/* Theme Selector - SEGURANÇA: Oculto para usuários com restrição de loja */}
            {!hasStoreRestriction && (
              <>
                <div className="h-4 w-px bg-white/10 mx-2" />
                <ThemeSelector />
              </>
            )}
          </div>
        </header>

        {/* Token Expiration Warning */}
        <TokenExpirationWarning />

        {/* Page content - transparent to show animated background */}
        {/* Ajuste de padding para acomodar a FilterSidebar à direita (quando visível) */}
        <main
          className="flex-1 p-4 md:p-6 overflow-x-hidden overflow-y-auto min-w-0 transition-[padding] duration-200"
          style={{ paddingRight: isSidebarHidden ? undefined : `${FILTER_SIDEBAR_WIDTH.collapsed + 24}px` }}
        >
          <div className="w-full max-w-[1920px] mx-auto overflow-x-hidden">
            <Outlet />
          </div>
        </main>

        {/* FilterSidebar global - fixa à direita (oculta em páginas com filtros próprios) */}
        {!isSidebarHidden && <FilterSidebar />}
      </SidebarInset>
      </SidebarProvider>

      {/* Version Badge - canto inferior direito */}
      <VersionBadge />
    </>
  );
}

